import { DataProxy } from '@apollo/client';

import {
  ListTimelineEntriesQuery,
  ListTimelineEntriesQueryVariables,
  MeasurementSystem,
  PageType,
  TimelineEntryType,
} from '../../../types/__generated__';
import useFullPageQuery from '../useFullPageQuery';
import query from './queries/listTimelineEntries.gql';

export function useListTimelineEntries(
  measurementSystem?: MeasurementSystem,
  entryType?: TimelineEntryType,
) {
  // B2C_048 - show hide fitness data
  // EntryType can be removed when experiment has ended
  // or added as a non experiment test if successful
  return useFullPageQuery<ListTimelineEntriesQuery>(query, {
    variables: { measurementSystem, entryType },
    context: {
      credentials: 'include',
    },
  });
}

const getEmptyPage = (slug: string, type: string) => ({
  id: '',
  legacyId: '',
  type: type.toUpperCase() as PageType,
  slug,
  url: '',
  title: '',
});

export function readTimelineEntries(
  pageType: string,
  pageSlug: string,
  cache: DataProxy,
  measurementSystem?: MeasurementSystem,
) {
  const result = cache.readQuery<
    ListTimelineEntriesQuery,
    ListTimelineEntriesQueryVariables
  >({
    query: query,
    variables: {
      type: pageType.toUpperCase() as PageType,
      slug: pageSlug,
      measurementSystem,
    },
  });

  const previousPage = result?.page ?? getEmptyPage(pageSlug, pageType);
  const previousOwner = result?.page?.owner;
  const previousTimeline = result?.page?.timeline ?? { totalCount: 0 };
  const previousTimelineNodes = result?.page?.timeline?.nodes ?? [];
  const previousPageInfo = result?.page?.timeline?.pageInfo ?? {
    startCursor: '',
    endCursor: '',
    hasNextPage: false,
    hasPreviousPage: false,
  };

  return {
    previousPage,
    previousOwner,
    previousTimeline,
    previousTimelineNodes,
    previousPageInfo,
  };
}

export function writeTimelineEntries(
  pageType: string,
  pageSlug: string,
  data: ListTimelineEntriesQuery,
  cache: DataProxy,
) {
  cache.writeQuery<ListTimelineEntriesQuery, ListTimelineEntriesQueryVariables>(
    {
      query: query,
      data,
      variables: {
        type: pageType.toUpperCase() as PageType,
        slug: pageSlug,
      },
    },
  );
}

// Cache update happens in the typePolicies, defined in cache.ts
export const fetchPage = (
  after: string,
  fetchMore: Function,
  first?: number,
): (() => Promise<void>) => {
  return () =>
    fetchMore({
      variables: { after, first },
    });
};
